import Vue from 'vue';
import Vuex from 'vuex';
import axios from 'axios';
import createPersistedState from 'vuex-persistedstate';

import notificator from '../services/notificator';
import socket from '../services/socket';
import router from '../router';
import auth from '../views/auth/store';
import lobby from '../views/lobby/store';
import game from '../views/game/store';
import layout from '../views/layout/store';
import utils from './utils';
import chat from '../components/chat/store';
import socketStore from './socket';

Vue.use(Vuex);

// TODO: refactor
const generateEmptyGameState = () => ({
  id: null,
  shortId: null,
  isKnowingTime: null,
  status: null,
  createdAt: null,
  startedAt: null,
  finishedAt: null,
  creator: {
    id: null,
    username: null,
    email: null,
    role: null,
  },
  startingOptions: {
    mordredMode: false,
    morganaPercivalMode: false,
    oberonMode: false,
    anonymousVotesMode: false,
    failFromServantsDisallowedMode: false,
  },
  isLocked: true,
  board: {
    id: null,
    minionsCount: null,
    quests: [],
  },
  players: [],
  executionTime: null,
  executor: {
    id: null,
    username: null,
    email: null,
    role: {
      id: null,
      title: null,
      description: null,
      team: null,
    },
  },
  executed: {
    player: {
      id: null,
      username: null,
      email: null,
      role: {
        id: null,
        title: null,
        description: null,
        team: null,
      },
    },
    isSubmitted: null,
    submittedAt: null,
  },
  teamsRejectedStreak: null,
  activeQuest: {
    number: null,
    failsCount: null,
    teamProposal: {
      isSubmitted: null,
      isApproved: null,
      approvesCount: null,
      rejectsCount: null,
      proposer: {
        id: null,
        username: null,
        email: null,
        role: null,
      },
      team: [],
      votes: [],
    },
    actions: [],
  },
  renewAfter: null,
});

export default new Vuex.Store({
  // is needed for the store to be immediately available
  // upon app start to write the chat messages into the store
  plugins: [
    createPersistedState({
      paths: [''],
    }),
  ],
  modules: {
    auth,
    lobby,
    game,
    layout,
    utils,
    chat,
    socket: socketStore,
  },
  state: {
    gameState: generateEmptyGameState(),
  },
  // TODO: cast values to booleans properly
  getters: {
    getGameState: (state) => state.gameState,
    getGameShortId: (state) => state.gameState.shortId,
    getGamePinCode: (state) =>
      state.gameState.shortId ? state.gameState.shortId.split('-')[1].substr(0, 4) : '',
    getStartingOptions: (state) => state.gameState.startingOptions || {},
    getIsKnowingTime: (state) => state.gameState.isKnowingTime,
    getRoles: (state) => state.gameState.roles,
    getPlayers: (state) => state.gameState.players,
    getMinionsCount: (state) => state.gameState.board.minionsCount,
    getServantsCount: (state, getters) => getters.getPlayers.length - getters.getMinionsCount,
    getQuests: (state) => state.gameState.board.quests,
    getActiveQuest: (state) => state.gameState.activeQuest,
    getTeamProposer: (state) => state.gameState.activeQuest.teamProposal.proposer,
    getProposedTeam: (state) => state.gameState.activeQuest.teamProposal.team,
    getTeamIsSubmitted: (state) => state.gameState.activeQuest.teamProposal.isSubmitted,
    getTeamIsApproved: (state) => state.gameState.activeQuest.teamProposal.isApproved,
    getTeamVotes: (state) => state.gameState.activeQuest.teamProposal.votes,
    getQuestActions: (state) => state.gameState.activeQuest.actions,
    getActiveQuestIsSubmitted: (state) => state.gameState.activeQuest.isSubmitted,
    getGameIsLocked: (state) => state.gameState.isLocked,
    getIsPlayersSwappingTime: (state, getters) => getters.gameIsCreated && getters.getGameIsLocked,
    getRenewAfter: (state) => state.gameState.renewAfter,
    getGameStatus: (state) => state.gameState.status,
    gameIsCreated: (state) => state.gameState.status === 'CREATED',
    gameIsStarted: (state) => state.gameState.status === 'STARTED',
    gameIsFinished: (state) => state.gameState.status === 'FINISHED',
    getMyself: (state, getters, rootState, rootGetters) => {
      const userId = rootGetters['auth/user'].id;

      return getters.getPlayers.find((p) => p.id === userId);
    },
    getGamesRejectedStreak: (state) => state.gameState.teamsRejectedStreak,
    getExecutor: (state) => state.gameState.executor,
    getExecuted: (state) => state.gameState.executed,
    getIsExecutionTime: (state) => !!state.gameState.executionTime,
    getActiveQuestPlayersNeeded: (state, getters) => {
      const quest = getters.getQuests.find(
        (quest) => quest.number === getters.getActiveQuest.number,
      );

      if (!quest) return 0;

      return quest.playersCount;
    },
    getCorrectAmountOfPlayersIsChosen: (state, getters) => {
      const quest = getters.getQuests.find(
        (quest) => quest.number === getters.getActiveQuest.number,
      );

      if (!quest) return false;

      return quest.playersCount === getters.getProposedTeam.length;
    },
    getIAmCreator: (state, getters, rootState, rootGetters) => {
      return state.gameState.creator.id === rootGetters['auth/user'].id;
    },
    playersAreChooseable: (state, getters, rootState, rootGetters) => {
      return (
        getters.getTeamProposer.id === rootGetters['auth/user'].id && !getters.getTeamIsSubmitted
      );
    },
    amAllowedToVote: (state, getters, rootState, rootGetters) => {
      return (
        getters.getTeamIsApproved === null &&
        !!getters.getTeamIsSubmitted &&
        !getters.getTeamVotes.find((player) => player.player.id === rootGetters['auth/user'].id)
      );
    },
    amAllowedToAct: (state, getters, rootState, rootGetters) => {
      return (
        !!getters.getTeamIsApproved &&
        !!getters.getProposedTeam.find((player) => player.id === rootGetters['auth/user'].id) &&
        !getters.getQuestActions.find((player) => player.id === rootGetters['auth/user'].id)
      );
    },
    amAllowedToExecute: (state, getters, rootState, rootGetters) => {
      return getters.getExecutor.id === rootGetters['auth/user'].id && getters.getIsExecutionTime;
    },
  },
  mutations: {
    setGameState: (state, gameState) => (state.gameState = gameState),
    setTeam: (state, team) => {
      state.gameState.activeQuest.teamProposal.team = team;
    },
    setTeamIsSubmitted: (state, isSubmitted) => {
      state.gameState.activeQuest.teamProposal.isSubmitted = isSubmitted;
    },
    clearProposedPlayers: (state) => {
      state.gameState.activeQuest.teamProposal.team = [];
    },
    markMeAsAlreadyVoted: (state, playerId) => {
      state.gameState.activeQuest.teamProposal.votes.push({
        player: { id: playerId },
      });
    },
    markAsAlreadyActed: (state, playerId) => {
      state.gameState.activeQuest.actions.push({
        id: playerId,
      });
    },
    markPlayerForExecution: (state, playerId) => {
      if (state.gameState.executor.id === playerId) {
        return;
      }

      state.gameState.executed = {
        player: { id: playerId },
      };
    },
  },
  actions: {
    fetchGameState: async ({ commit, dispatch }, gameId) => {
      try {
        const { data } = await axios.get(`/game/${gameId}/state`);

        if (data.renewAfter !== null) {
          setTimeout(() => {
            dispatch('fetchGameState', gameId);
          }, (data.renewAfter + 1) * 1000);
        }

        commit('setGameState', data);
      } catch (e) {
        console.log({ ...e });

        router.push({ name: 'lobby' });

        notificator.error('Game state could not be fetched.');
      }
    },
    applyNodeState: ({ commit, getters }, { senderId, state }) => {
      // disallow a spectator to influence the node state
      const player = getters.getPlayers.find((p) => p.id === senderId);
      if (!player) {
        return;
      }

      if (!state.stateIsLocked) {
        commit(
          'setTeam',
          state.team.map((playerId) => ({ id: playerId })),
        );
      }

      if (!state.stateIsLocked && state.executionTargetId) {
        commit('markPlayerForExecution', state.executionTargetId);
      }
    },
    //
    //  TEAM PROPOSAL
    //
    clearProposedTeam: (_, gameId) => {
      socket.emitClearProposedTeam(gameId);
    },
    proposeTeam: async ({ state, commit }, payload) => {
      commit('setTeamIsSubmitted', true);

      try {
        await axios.put(`/game/${payload.gameId}/propose-team`, {
          team: state.gameState.activeQuest.teamProposal.team.map((player) => player.id),
          submit: payload.submit,
        });

        socket.emitFetchState(payload.gameId);
      } catch (e) {
        console.log({ ...e });

        notificator.error('The team could not be submitted.');
      }
    },
    //
    // APPROVE / REJECTS
    //
    teamVote: async ({ commit, rootGetters }, payload) => {
      commit('markMeAsAlreadyVoted', rootGetters['auth/user'].id);

      try {
        await axios.put(`/game/${payload.gameId}/team-vote`, {
          vote: payload.vote,
        });

        socket.emitFetchState(payload.gameId);
      } catch (e) {
        console.log({ ...e });

        notificator.error('The vote could not be submitted.');
      }
    },
    //
    // SUCCESS / FAILS
    //
    questAction: async ({ commit, rootGetters }, payload) => {
      commit('markAsAlreadyActed', rootGetters['auth/user'].id);

      try {
        await axios.put(`/game/${payload.gameId}/quest-action`, {
          action: payload.action,
        });

        socket.emitFetchState(payload.gameId);
      } catch (e) {
        console.log({ ...e });

        notificator.error('The quest action could not be submitted.');
      }
    },
    //
    // PLAYER EXECUTION
    //
    executePlayer: async ({ getters }, payload) => {
      try {
        await axios.put(`/game/${payload.gameId}/execute`, {
          userId: getters.getExecuted.player.id,
          submit: payload.submit,
        });

        socket.emitFetchState(payload.gameId);
      } catch (e) {
        console.log({ ...e });

        notificator.error('The execution candidate could not be submitted.');
      }
    },
  },
});
